import { useEffect, useRef } from "react";
import styles from "./styles/PosTicketSearch.module.css";
import SliderToggle from "../../../components/ui/slider_toggle/SliderToggle";
import InputText from "../../../components/ui/input_text/InputText";
import { useTranslation } from "react-i18next";
import { toCamelCase } from "../../../utils/utils";
import InputDate from "../../../components/ui/input_date/InputDate";

const PosSearchGroup = ({
    handleChange, // {Function} - Function to handle change events
    handleNestedChange, // {function} - Function to handle nested change events
    searchGroup, // {Object} - Object containing the search group data
    hoverFlag, // {Boolean} - Flag indicating hover state
    groupId, // {Integer} - Identifier for the group
    fields, // {Array} - Fields in the search group
    groupIdx, // {Integer} - Index of the search group
    searchInput, // {Object} - Object containing the inputs of the search group
    setSearchInput, // {Function} - Function to set the search group input
    reset, // {Boolean} - Flag indicating if the fields are reset
}) => {
    const { t } = useTranslation();
    const groupRefs = useRef({});

    // Set the focus on the first element whenever the group changes or if the fields are reseted
    // on field reset a flagh (reset) is set to true which forces the useEffect to fire, the flag is set back to false
    useEffect(() => {
        if (parseInt(groupId) === parseInt(searchGroup)) {
            setTimeout(() => {
                const firstInput = groupRefs.current[groupId];
                if (firstInput && firstInput.focus) {
                    firstInput.focus();
                }
            }, 0);
        }
    }, [searchGroup, reset]);

    /**
     * Create a detecting string for validation
     * The steps are as follows:
     * 1- Get the quantity of the fields.
     * 2- Loop through the fields and construct the field identifier key ( it should start with "SIField-")
     * 3- Add the formated string with the field identifier key to the detection string
     * 4- Add the relation to the next field (only if the field is not the last one)
     * The result should look like this:
     *      or relation:  !(searchInput?.groups?.[0]?.fields?.[0]?.["SIField-0-1-ticketno"] || searchInput?.groups?.[0]?.fields?.[1]?.["SIField-0-2-plate"])
     *      and relation: !(searchInput?.groups?.[1]?.fields?.[0]?.["SIField-1-1-ticketno"] && searchInput?.groups?.[1]?.fields?.[1]?.["SIField-1-2-plate"])
     * @returns {String} - the detection string used for validation
     */
    const getDetectionString = () => {
        let detection = ``;
        const fieldsQuantity = fields.length;

        fields.forEach((field, idx2) => {
            // Construct the field identifier key by combining"
            // 1- key prifix - "SIField-"
            // 2- group index - groupIdx
            // 3- field identifier - field?.id
            // 4- field name in camel case - field.fieldName
            const keyName = `SIField-${groupIdx}-${field?.id}-${toCamelCase(
                field.fieldName
            )}`;
            // Add the formated string to the detection string
            // This string is actually the path to the fields value in the searchInputs object
            // Example: searchInput?.groups?.[0]?.fields?.[0]?.["SIField-0-1-ticketno"]
            detection += `searchInput?.groups?.[${groupIdx}]?.fields?.[${idx2}]?.["${keyName}"]`;
            // Add the relation between fields
            // Only if the field is not the last one
            // the value is taken from the rel key for each field ine the search group
            if (idx2 < fieldsQuantity - 1) {
                detection +=
                    searchInput?.groups?.[groupIdx]?.fields?.[idx2]?.rel ===
                    "and"
                        ? " && "
                        : " || ";
            }
        });
        detection = `!(${detection})`;
        return detection;
    };

    const detection = getDetectionString();
    // Update the detection string for each search group
    useEffect(() => {
        setSearchInput((prevState) => ({
            ...prevState,
            groups: prevState.groups.map((group) =>
                group.id === parseInt(groupId)
                    ? { ...group, detectionString: detection } // Update the detectionString of the matched group
                    : group
            ),
        }));
    }, [detection]);

    return (
        <div
            // Inactive group style
            className={`${styles.input_group_container} ${
                parseInt(groupId) !== parseInt(searchGroup)
                    ? styles.inactive
                    : ""
            }  flex-container`}
        >
            {/* The activate slider */}
            <div className={`${styles.slider_container} `}>
                <SliderToggle
                    RadioOption={groupId}
                    inputValue={searchGroup}
                    inputId={"searchGroup"}
                    onClickFunction={handleChange}
                    type={"radio"}
                    data={detection} // The detection string for the group
                />
            </div>

            <div>
                {fields &&
                    fields.length > 0 &&
                    fields.map((field, idx2) => {
                        const keyName = `SIField-${groupIdx}-${
                            field?.id
                        }-${toCamelCase(field.fieldName)}`;

                        return searchInput?.groups?.[groupIdx]?.fields?.[idx2]
                            ?.type === "date" ? (
                            <InputDate
                                key={`T-${groupId}-F${field?.id}`}
                                // dynamic ref for setting the focus to the first element
                                ref={
                                    idx2 === 0
                                        ? (el) =>
                                              (groupRefs.current[groupId] = el)
                                        : null
                                }
                                label={t(field?.label)}
                                inputId={keyName}
                                data={
                                    searchInput?.groups?.[groupIdx]?.fields?.[
                                        idx2
                                    ]?.rel
                                } // relation to the next field (by default or, ignored for the last field)
                                inputValue={
                                    searchInput?.groups?.[groupIdx]?.fields?.[
                                        idx2
                                    ]?.[keyName]
                                }
                                handleChangeFunction={(event) =>
                                    handleNestedChange(
                                        event,
                                        `groups.${groupIdx}.fields.${idx2}.${keyName}`
                                    )
                                }
                                required={
                                    parseInt(groupId) ===
                                        parseInt(searchGroup) &&
                                    hoverFlag &&
                                    eval(detection) // eval function will evaluate the string and return Boolean
                                } // Required validation (validate the active group only)
                                disable={
                                    !(
                                        parseInt(groupId) ===
                                        parseInt(searchGroup)
                                    )
                                }
                                toToday={true}
                                clearTrigger={reset}
                            />
                        ) : (
                            <InputText
                                key={`T-${groupId}-F${field?.id}`}
                                // dynamic ref for setting the focus to the first element
                                ref={
                                    idx2 === 0
                                        ? (el) =>
                                              (groupRefs.current[groupId] = el)
                                        : null
                                }
                                letterSpacing={".2rem"}
                                placeholder={t(`required field`)}
                                label={t(field?.label)}
                                inputId={keyName}
                                data={
                                    searchInput?.groups?.[groupIdx]?.fields?.[
                                        idx2
                                    ]?.rel
                                } // relation to the next field (by default or, ignored for the last field)
                                inputValue={
                                    searchInput?.groups?.[groupIdx]?.fields?.[
                                        idx2
                                    ]?.[keyName]
                                }
                                handleChangeFunction={(event) =>
                                    handleNestedChange(
                                        event,
                                        `groups.${groupIdx}.fields.${idx2}.${keyName}`
                                    )
                                }
                                required={
                                    parseInt(groupId) ===
                                        parseInt(searchGroup) &&
                                    hoverFlag &&
                                    eval(detection) // eval function will evaluate the string and return Boolean
                                } // Required validation (validate the active group only)
                                disable={
                                    !(
                                        parseInt(groupId) ===
                                        parseInt(searchGroup)
                                    )
                                }
                            />
                        );
                    })}
            </div>
        </div>
    );
};

export default PosSearchGroup;
